/**
 * SavedConnectionsList Component
 *
 * Displays saved connections in the Connections Dialog.
 * Shows recent connections first, with collapsible "Other" section.
 * Active connections show quick actions.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { PlusCircle, ChevronRight, Search } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Connection } from '../../../types/electron';
import { useConnectionStore } from '../store/connectionStore';
import { useDisconnectWithConfirmation } from '../hooks/useDisconnectWithConfirmation';
import { DisconnectConfirmDialog } from './DisconnectConfirmDialog';
import { ConnectionErrorNotice } from './ConnectionErrorNotice';
import type { ConnectionErrorFixRetry } from '../utils/connectionErrorFix';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { ConnectionCardCompact } from './ConnectionCardCompact';
import { QuickActionsBarDialog } from './QuickActionsBarDialog';
import { sortConnectionsByDisplayOrder } from '../utils/connectionDisplayOrder';
import { useConnectionListKeyboard } from '../hooks/useConnectionListKeyboard';
import { safeConnectionName } from '../../../types/safeConnectionDisplay';

type SavedConnectionsListProps = {
  onAddNew: () => void;
  showAddButton?: boolean;
  onConnectSuccess?: () => void;
  onEdit?: (connection: Connection) => void;
};

const RECENT_CONNECTIONS_COUNT = 4;

export function SavedConnectionsList({
  onAddNew,
  showAddButton = true,
  onConnectSuccess,
  onEdit,
}: SavedConnectionsListProps) {
  const [error, setError] = useState<{
    message: string;
    connection?: Connection;
    /** Structured driver error code from the store, when the engine preserved one. */
    errorCode?: string;
  } | null>(null);
  const [fixBusy, setFixBusy] = useState(false);
  // Synchronous double-click guard: `fixBusy` state lands a render late, so
  // two fast clicks could otherwise start two concurrent patched dials.
  const fixBusyRef = useRef(false);
  const [otherExpanded, setOtherExpanded] = useState(false);
  const { track } = useAnalytics();
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const connections = useConnectionStore((state) => state.connections);
  const activeConnections = useConnectionStore((state) => state.activeConnections);
  const connectionOperationById = useConnectionStore((state) => state.connectionOperationById);
  const connectionErrorById = useConnectionStore((state) => state.connectionErrorById);
  const connectToDatabase = useConnectionStore((state) => state.connectToDatabase);

  const {
    dialogState: disconnectDialogState,
    initiateDisconnect,
    confirmDisconnect,
    cancelDisconnect,
  } = useDisconnectWithConfirmation();

  useEffect(() => {
    // Delay focus slightly so the dialog open animation doesn't steal it
    const timer = setTimeout(() => searchInputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, []);

  // Get active connection IDs for sorting
  const activeConnectionIds = useMemo(
    () => new Set(activeConnections.map((ac) => ac.id)),
    [activeConnections]
  );

  // Filter connections by search query
  const filteredConnections = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return connections;
    return connections.filter((c) => {
      const alias = c.alias?.toLowerCase() ?? '';
      const server = c.server?.toLowerCase() ?? '';
      return alias.includes(q) || server.includes(q);
    });
  }, [connections, searchQuery]);

  // Sort and split connections
  const { recentConnections, otherConnections } = useMemo(() => {
    const sorted = sortConnectionsByDisplayOrder(filteredConnections, activeConnectionIds);
    return {
      recentConnections: sorted.slice(0, RECENT_CONNECTIONS_COUNT),
      otherConnections: sorted.slice(RECENT_CONNECTIONS_COUNT),
    };
  }, [filteredConnections, activeConnectionIds]);

  // Flat list of all currently visible connections for keyboard navigation
  const visibleConnections = useMemo(() => {
    const showOther = otherExpanded || !!searchQuery.trim();
    return showOther ? [...recentConnections, ...otherConnections] : [...recentConnections];
  }, [recentConnections, otherConnections, otherExpanded, searchQuery]);

  const isConnectionActive = (connectionId: string): boolean => {
    return activeConnectionIds.has(connectionId);
  };

  const getConnectionStatus = (connection: Connection) => {
    const operation = connectionOperationById[connection.id];
    const error = connectionErrorById[connection.id];
    const activeConnection = activeConnections.find((ac) => ac.id === connection.id);

    if (operation === 'connecting') return { status: 'connecting' as const, database: undefined };
    if (operation === 'disconnecting') return { status: 'disconnecting' as const, database: activeConnection?.connection.database };
    if (error) return { status: 'error' as const, database: undefined, errorMessage: error };
    if (activeConnection?.status === 'connected') {
      return { status: 'connected' as const, database: activeConnection.connection.database };
    }
    return { status: 'disconnected' as const, database: undefined };
  };

  const handleConnect = useCallback(
    async (connection: Connection) => {
      try {
        setError(null);
        const success = await connectToDatabase(connection);

        if (success) {
          onConnectSuccess?.();
        } else {
          const storeState = useConnectionStore.getState();
          const storeError = storeState.connectionErrorById[connection.id];
          setError({
            message: storeError || `Failed to connect to ${safeConnectionName(connection)}`,
            connection,
            errorCode: storeState.connectionErrorCodeById[connection.id],
          });
        }
      } catch (err) {
        console.error('Connection action error:', err);
        setError({
          message: `Error connecting to ${safeConnectionName(connection)}`,
          connection,
        });
      }
    },
    [connectToDatabase, onConnectSuccess]
  );

  const handleDisconnect = useCallback(
    (connection: Connection) => {
      initiateDisconnect(connection.id, safeConnectionName(connection));
    },
    [initiateDisconnect]
  );

  /**
   * One-click fix chip on the error notice (e.g. "Retry trusting the server
   * certificate" for a saved connection whose server has a self-signed cert).
   * Retries with the flipped setting; `connectToDatabase` persists the
   * connection record ONLY when the retry succeeds, so a failed retry leaves
   * the saved settings untouched. Always a visible user action — never an
   * automatic fallback.
   */
  const handleApplyFix = useCallback(
    async (fix: ConnectionErrorFixRetry) => {
      const connection = error?.connection;
      if (!connection || fixBusyRef.current) return;

      // Already connected (e.g. a parallel connect landed after the error
      // rendered): nothing to retry, and reporting fix_result success would
      // credit the chip for a dial it never made.
      const alreadyConnected = useConnectionStore
        .getState()
        .activeConnections.some((c) => c.id === connection.id && c.status === 'connected');
      if (alreadyConnected) {
        setError(null);
        onConnectSuccess?.();
        return;
      }

      fixBusyRef.current = true;
      const patched: Connection = { ...connection, ...fix.apply };
      setError(null);
      setFixBusy(true);
      try {
        // `keepOnFailure` (expired-password fix): by now the SERVER-side
        // password has already changed, so the new value is the only one that
        // can ever connect again. Persist it (encrypted by main) BEFORE the
        // retry — a failed retry (timeout, wrong service too) must not lose
        // it, or every later connect dies on ORA-01017 with no fix offered.
        //
        // The save carries `passwordEdited` when it carries a new password
        // (round 5, F6): a password edit is a fact about THIS save that no
        // comparison of the records can see (main holds ciphertext), and it is
        // what makes both sides invalidate a pool authenticated with the old
        // credential. Without it the retry could be served a pool dialled with
        // the password the server has just expired.
        if (fix.keepOnFailure) {
          await useConnectionStore
            .getState()
            .updateConnection(patched, { passwordEdited: fix.apply.password !== undefined });
        }

        // Known narrow window: if an UNPATCHED dial for this id is still in
        // flight, the store joins it instead of dialing with `patched`, and a
        // success here would be the old dial's. Not detectable from outside
        // the store (its in-flight map is module-private); accepted — the
        // chip only renders after a connect has settled with an error, so the
        // join requires the user to race a manual reconnect against the chip.
        const success = await connectToDatabase(patched);
        track('feature:connection_fix_result', {
          engine: connection.engine ?? 'unknown',
          fix: fix.kind,
          success,
          surface: 'connections-list',
        });
        if (success) {
          onConnectSuccess?.();
        } else {
          const storeState = useConnectionStore.getState();
          const storeError = storeState.connectionErrorById[connection.id];
          setError({
            message: storeError || `Failed to connect to ${safeConnectionName(connection)}`,
            // After a persisted password change, follow-up fixes must dial
            // with the NEW credential — sanitized: the stored password is
            // lazily decrypted at connect time, plaintext never lingers here.
            connection: fix.keepOnFailure
              ? { ...connection, password: undefined, hasStoredPassword: true }
              : connection,
            errorCode: storeState.connectionErrorCodeById[connection.id],
          });
        }
      } finally {
        // The password-fix path routed a plaintext-carrying record through
        // `updateConnection`/`connectToDatabase`, both of which write it into
        // `state.connections` (the store's own rule is plaintext never lives
        // there). Re-read the sanitized records from main to purge it.
        if (fix.keepOnFailure) {
          await useConnectionStore.getState().loadConnections();
        }
        fixBusyRef.current = false;
        setFixBusy(false);
      }
    },
    [error, connectToDatabase, onConnectSuccess, track]
  );

  const {
    focusedIndex,
    setFocusedIndex,
    handleSearchKeyDown,
    handleCardKeyDown,
  } = useConnectionListKeyboard({
    visibleConnections,
    activeConnectionIds,
    searchInputRef,
    listRef,
    onConnect: handleConnect,
    onDisconnect: handleDisconnect,
  });

  const renderConnectionCard = (conn: Connection, globalIndex: number) => {
    const { status, database, errorMessage } = getConnectionStatus(conn);
    const isActive = isConnectionActive(conn.id);

    return (
      <ConnectionCardCompact
        key={conn.id}
        connection={conn}
        status={status}
        database={database}
        errorMessage={errorMessage}
        onConnect={() => handleConnect(conn)}
        onDisconnect={() => handleDisconnect(conn)}
        onEdit={onEdit ? () => onEdit(conn) : undefined}
        isFocused={focusedIndex === globalIndex}
        onKeyDown={(e) => handleCardKeyDown(e, globalIndex)}
        quickActions={
          isActive ? (
            <QuickActionsBarDialog
              connectionId={conn.id}
              database={database}
              onClose={onConnectSuccess}
            />
          ) : undefined
        }
      />
    );
  };

  // Narrowed for the editor-route fix chips: the notice needs a stable
  // reference to the failing connection to hand to `onEdit`.
  const failingConnection = error?.connection;

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="saved-connections-list">
      {connections.length > RECENT_CONNECTIONS_COUNT && (
        <div className="relative mb-3 mx-1 flex-shrink-0">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            ref={searchInputRef}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            onFocus={() => setFocusedIndex(-1)}
            placeholder="Search by name, alias or host..."
            className="pl-8 h-8 text-sm"
            data-testid="connections-search-input"
          />
        </div>
      )}

      {error && (
        <ConnectionErrorNotice
          error={error.message}
          connection={error.connection}
          errorCode={error.errorCode}
          surface="connections-list"
          onApplyFix={(fix) => void handleApplyFix(fix)}
          onEditConnection={
            onEdit && failingConnection
              ? () => {
                  // Hand the LIVE record, not the error-time snapshot: the
                  // user may have already edited the connection via the
                  // card's own Edit button, and seeding the editor with the
                  // snapshot would silently revert that save on Save. A
                  // record deleted since the failure opens nothing — seeding
                  // the editor with the snapshot would resurrect it on Save.
                  const live = connections.find((c) => c.id === failingConnection.id);
                  if (live) onEdit(live);
                  // The notice describes the dial this editor visit
                  // supersedes — keeping it would pair a stale error with
                  // whatever the user saves next.
                  setError(null);
                }
              : undefined
          }
          fixBusy={fixBusy}
        />
      )}

      {connections.length === 0 ? (
        <div className="text-center py-6">
          <p className="text-muted-foreground mb-4">No saved connections found</p>
          <Button onClick={onAddNew} className="active:scale-[0.96] transition-[color,box-shadow,scale]">
            Add New Connection
          </Button>
        </div>
      ) : (
        <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
          {/* No results for search */}
          {filteredConnections.length === 0 && searchQuery.trim() && (
            <div className="text-center py-6">
              <p className="text-muted-foreground text-sm">No connections matching "{searchQuery.trim()}"</p>
            </div>
          )}

          {/* Recent connections section */}
          {recentConnections.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">Recent</h4>
              <div className="space-y-3">
                {recentConnections.map((conn, i) => renderConnectionCard(conn, i))}
              </div>
            </div>
          )}

          {/* Other connections section (collapsible) */}
          {otherConnections.length > 0 && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setOtherExpanded(!otherExpanded)}
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full text-left tabular-nums"
              >
                <ChevronRight
                  className={cn(
                    'h-4 w-4 transition-transform duration-150',
                    otherExpanded && 'rotate-90',
                  )}
                />
                Other ({otherConnections.length})
              </button>
              {(otherExpanded || searchQuery.trim()) && (
                <div className="space-y-3 animate-in fade-in slide-in-from-top-1 duration-150">
                  {otherConnections.map((conn, i) => renderConnectionCard(conn, recentConnections.length + i))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showAddButton && connections.length > 0 && (
        <div className="pt-4 mt-4 border-t border-border flex-shrink-0">
          <Button
            variant="outline"
            onClick={onAddNew}
            className="flex items-center gap-2 active:scale-[0.96] transition-[color,box-shadow,scale]"
          >
            <PlusCircle className="h-4 w-4" />
            Add New Connection
          </Button>
        </div>
      )}

      {disconnectDialogState && (
        <DisconnectConfirmDialog
          open={true}
          connectionName={disconnectDialogState.connectionName}
          affectedTabs={disconnectDialogState.affectedTabs}
          onConfirm={confirmDisconnect}
          onCancel={cancelDisconnect}
        />
      )}
    </div>
  );
}
